/**
 * Models List Page
 */

import {
  Card,
  CardBody,
  Chip,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TableHeader,
  TableRow,
} from '@heroui/react';
import { createFileRoute } from '@tanstack/react-router';
import { Activity } from 'lucide-react';

import { useModels } from '@/lib/api/hooks/models';
import { formatRelativeTime } from '@/lib/design-system';

export const Route = createFileRoute('/models/')({
  component: ModelsPage,
});

function ModelsPage() {
  const { data: models, isLoading, error } = useModels();

  return (
    <div className="container-page py-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold">Models</h1>
        <p className="text-default-500">View and manage your trained models</p>
      </div>

      {isLoading && (
        <div className="flex justify-center py-12">
          <Spinner size="lg" />
        </div>
      )}

      {error && (
        <Card>
          <CardBody className="text-center text-danger">
            Failed to load models. Please try again.
          </CardBody>
        </Card>
      )}

      {!isLoading && !error && models && models.length === 0 && (
        <Card>
          <CardBody className="flex flex-col items-center gap-4 py-12 text-center">
            <div className="rounded-full bg-default-100 p-4">
              <Activity className="h-8 w-8 text-default-400" />
            </div>
            <div>
              <h3 className="text-lg font-medium">No models yet</h3>
              <p className="text-default-500">
                Train a model from the Training page to get started.
              </p>
            </div>
          </CardBody>
        </Card>
      )}

      {!isLoading && !error && models && models.length > 0 && (
        <Table
          aria-label="Models table"
          selectionMode="none"
          classNames={{
            wrapper: 'shadow-none border border-default-200',
          }}
        >
          <TableHeader>
            <TableColumn>NAME</TableColumn>
            <TableColumn>ARTIFACT TYPES</TableColumn>
            <TableColumn>STATUS</TableColumn>
            <TableColumn>CREATED</TableColumn>
          </TableHeader>
          <TableBody>
            {models.map((model) => (
              <TableRow
                key={model.id}
                className="cursor-pointer transition-colors hover:bg-default-50"
                onClick={() => {
                  window.location.href = `/models/${model.id}`;
                }}
              >
                <TableCell>
                  <div>
                    <p className="font-medium">{model.name || `Model ${model.id.slice(0, 8)}`}</p>
                    {model.description && (
                      <p className="text-sm text-default-500 truncate max-w-xs">
                        {model.description}
                      </p>
                    )}
                  </div>
                </TableCell>
                <TableCell>
                  <div className="flex flex-wrap gap-1">
                    {model.artifact_types?.slice(0, 3).map((type, i) => (
                      <Chip key={i} size="sm" variant="flat">
                        {type}
                      </Chip>
                    ))}
                    {(model.artifact_types?.length ?? 0) > 3 && (
                      <Chip size="sm" variant="flat">
                        +{(model.artifact_types?.length ?? 0) - 3}
                      </Chip>
                    )}
                  </div>
                </TableCell>
                <TableCell>
                  <Chip
                    size="sm"
                    color={model.is_active ? 'success' : 'default'}
                    variant="flat"
                  >
                    {model.is_active ? 'Active' : 'Archived'}
                  </Chip>
                </TableCell>
                <TableCell className="text-default-500">
                  {formatRelativeTime(new Date(model.created_at))}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
}
